'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import AppLogo from '@/components/ui/AppLogo';
import {
  EyeIcon,
  EyeSlashIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  UserGroupIcon,
  BriefcaseIcon,
} from '@heroicons/react/24/outline';

interface LoginForm {
  email: string;
  password: string;
  rememberMe: boolean;
}

const demoCredentials = [
  { role: 'Super Admin', email: 'admin@palipaint.in', password: 'PaliAdmin@2026' },
  { role: 'Manager', email: 'manager@palipaint.in', password: 'PaliMgr@2026' },
];

const features = [
  { icon: ChartBarIcon, title: 'Complete Lead Pipeline', desc: 'Track every lead from inquiry to completed job' },
  { icon: BriefcaseIcon, title: 'Job Lifecycle Management', desc: 'Assign providers, schedule jobs, monitor progress' },
  { icon: UserGroupIcon, title: 'Provider Network', desc: 'Manage 47+ verified painting professionals' },
  { icon: ShieldCheckIcon, title: 'Role-Based Access', desc: 'Secure multi-staff admin with granular permissions' },
];

export default function LoginPage() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [loginError, setLoginError] = useState('');

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<LoginForm>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleAutofill = (cred: typeof demoCredentials[0]) => {
    setValue('email', cred.email);
    setValue('password', cred.password);
    setLoginError('');
  };

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    setLoginError('');
    // Backend integration point: POST /api/auth/login
    await new Promise(r => setTimeout(r, 1200));
    const valid = demoCredentials.some(c => c.email === data.email && c.password === data.password);
    if (valid) {
      localStorage.setItem('palipaint_auth', JSON.stringify({ email: data.email, role: data.email.includes('manager') ? 'Manager' : 'Super Admin' }));
      router.push('/admin-dashboard');
    } else {
      setLoginError('Invalid credentials — use the demo accounts below to sign in');
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-10 relative overflow-hidden"
        style={{ background: 'linear-gradient(145deg, #111827 0%, #1f2937 50%, #111827 100%)' }}>
        {/* Background paint strokes decorative */}
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #F97316 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />
        <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #F97316 0%, transparent 70%)', transform: 'translate(-30%, 30%)' }} />

        {/* Logo */}
        <div className="flex items-center gap-3 relative z-10">
          <AppLogo size={44} />
          <div>
            <span className="font-bold text-white text-2xl tracking-tight">PaliPaint</span>
            <p className="text-orange-400 text-xs font-medium tracking-widest uppercase">Professional Painting Services</p>
          </div>
        </div>

        {/* Main content */}
        <div className="relative z-10 space-y-8">
          <div>
            <h1 className="text-4xl font-bold text-white leading-tight">
              Manage your entire<br />
              <span className="text-primary">painting business</span><br />
              from one place.
            </h1>
            <p className="text-gray-400 mt-4 text-base leading-relaxed">
              PaliPaint Admin gives you complete control over leads, jobs, providers, payments and commissions — all in a single professional dashboard.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3">
            {features.map((feat) => {
              const FeatIcon = feat.icon;
              return (
                <div key={`feat-${feat.title}`} className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                  <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <FeatIcon className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">{feat.title}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{feat.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-white font-tabular">1,247</p>
              <p className="text-xs text-gray-500">Total Leads</p>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div className="text-center">
              <p className="text-2xl font-bold text-white font-tabular">892</p>
              <p className="text-xs text-gray-500">Jobs Done</p>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div className="text-center">
              <p className="text-2xl font-bold text-primary font-tabular">₹18.4L</p>
              <p className="text-xs text-gray-500">Revenue</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 bg-gray-50">
        <div className="w-full max-w-md space-y-6">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <AppLogo size={40} />
            <span className="font-bold text-foreground text-xl">PaliPaint</span>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-foreground">Admin Login</h2>
            <p className="text-muted-foreground text-sm mt-1">Sign in to access the PaliPaint admin panel</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="admin@palipaint.in"
                className={`w-full px-4 py-2.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all ${errors.email ? 'border-error' : 'border-border'}`}
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                })}
              />
              {errors.email && (
                <p className="text-xs text-error mt-1">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="block text-sm font-medium text-foreground">
                  Password
                </label>
                <button type="button" className="text-xs text-primary hover:underline font-medium">
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  className={`w-full px-4 py-2.5 pr-10 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all ${errors.password ? 'border-error' : 'border-border'}`}
                  {...register('password', { required: 'Password is required', minLength: { value: 6, message: 'Min 6 characters' } })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-error mt-1">{errors.password.message}</p>
              )}
            </div>

            {/* Remember me */}
            <div className="flex items-center gap-2">
              <input
                id="rememberMe"
                type="checkbox"
                className="w-4 h-4 rounded border-border accent-primary"
                {...register('rememberMe')}
              />
              <label htmlFor="rememberMe" className="text-sm text-muted-foreground">
                Keep me signed in for 30 days
              </label>
            </div>

            {/* Error */}
            {loginError && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                <p className="text-sm text-red-700">{loginError}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 rounded-lg btn-primary font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              style={{ minHeight: '42px' }}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Signing in...
                </>
              ) : 'Sign in to Admin Panel'}
            </button>
          </form>

          {/* Demo credentials */}
          <div className="border border-border rounded-xl overflow-hidden">
            <div className="bg-gray-50 px-4 py-2.5 border-b border-border">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Demo Credentials</p>
            </div>
            <div className="divide-y divide-border">
              {demoCredentials.map((cred) => (
                <div key={`cred-${cred.role}`} className="px-4 py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-foreground">{cred.role}</p>
                    <p className="text-xs text-muted-foreground truncate">{cred.email}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleCopy(`${cred.email} / ${cred.password}`, cred.role)}
                      className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      title="Copy credentials"
                    >
                      {copiedField === cred.role ? (
                        <CheckIcon className="w-3.5 h-3.5 text-success" />
                      ) : (
                        <ClipboardDocumentIcon className="w-3.5 h-3.5" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAutofill(cred)}
                      className="px-2.5 py-1 text-xs font-medium text-primary border border-primary/30 rounded-md hover:bg-primary/5 transition-colors"
                    >
                      Use
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-center text-xs text-muted-foreground">
            By signing in, you agree to PaliPaint&apos;s{' '}
            <span className="text-primary hover:underline cursor-pointer">Terms of Service</span>
            {' '}and{' '}
            <span className="text-primary hover:underline cursor-pointer">Privacy Policy</span>
          </p>
        </div>
      </div>
    </div>
  );
}